import ChevronDown from '@mdi/svg/svg/chevron-down.svg?react';
import ChevronUp from '@mdi/svg/svg/chevron-up.svg?react';
import { Collapsible, Flex, MDIIcon } from '@solinkcorp/solink-components';
import EventActions from 'actions/EventActions';
import { type DownloadOption } from 'components/PublicPages/types/camera.types';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { type CallhomeCamera } from 'utils/CameraUtil';
import { type ThumbnailType } from 'vd-constants';
import PlayerCameraList from '../VideoPlayer/components/PlayerCameraList';
import { type PlayingCamerasController } from '../VideoPlayer/controller/PlayingCamerasController';
import styles from './PlayerCameraSlidePicker.module.sass';

interface PlayerCameraSlidePickerProps {
  cameras: CallhomeCamera[];
  isOpenByDefault?: boolean;
  playingCamerasController: PlayingCamerasController;
  cameraThumbnailType?: ThumbnailType;
  multiPlayDisabled?: boolean;
  onUpdatePlayerCamera?: (id: string) => void;
  downloadOptionsByCameraId?: {
    [key: string]: DownloadOption;
  } | null;
  isCollapsible?: boolean;
  /**
   * Optional override for the default playCamera behaviour so picking a camera
   * also seeks to that camera's first-motion time minus the VerifEye pre-roll.
   */
  playCamera?: (camera: CallhomeCamera) => void;
}

/**
 * This component is to render a horizontal list of selectable cameras for the player.
 * It's collapsible and can scroll horizontally.
 */
const PlayerCameraSlidePicker: React.FC<PlayerCameraSlidePickerProps> = (props) => {
  const {
    cameras,
    isOpenByDefault = true,
    playingCamerasController,
    isCollapsible,
    playCamera: playCameraOverride,
    ...rest
  } = props;
  const locale = useLocale();
  const [isOpen, setIsOpen] = React.useState(isOpenByDefault);

  const togglePicker = () => setIsOpen(!isOpen);

  const playCamera =
    playCameraOverride ??
    ((camera: CallhomeCamera) => {
      EventActions.clearActiveEvent();
      playingCamerasController.setPlayingCamera(camera);
    });

  return (
    <div className={styles['player-camera-slide-picker']}>
      {isCollapsible && (
        <Flex alignItems="center" className={styles.collapsibleToggle} onClick={togglePicker}>
          <MDIIcon icon={isOpen ? ChevronUp : ChevronDown} />
          <div>{locale.camera.cameras}</div>
        </Flex>
      )}
      <Collapsible isOpen={isOpen}>
        <Flex className={styles.scrollContainer} flexWrap="nowrap" width="100%">
          <PlayerCameraList
            cameras={cameras}
            playCamera={playCamera}
            playingCamerasController={playingCamerasController}
            layout="horizontal"
            {...rest}
          />
        </Flex>
      </Collapsible>
    </div>
  );
};

export default React.memo(PlayerCameraSlidePicker);
